import { useEffect, useRef, useState } from "react";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type State = {
  eligible: boolean;
  configured?: boolean;
  existing?: boolean;
  extensionDigits?: string;
  connectionVersion?: number;
  reason?: string;
};

export function SoftphoneInstallPanel({
  api,
  accountId,
  deviceId,
  csrf,
  onInstalled,
}: {
  api: Api;
  accountId: string;
  deviceId: string;
  csrf?: string | undefined;
  onInstalled: () => void;
}) {
  const path = `/admin/accounts/${accountId}/equipment/${deviceId}/softphone-install`;
  const [state, setState] = useState<State | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const command = useRef({ path: "", key: "" });
  const active = useRef(false);
  const lock = useRef(false);
  useEffect(() => {
    active.current = true;
    const abort = new AbortController();
    setState(null);
    setError("");
    setNotice("");
    setConfirmed(false);
    void api<State>(path, { signal: abort.signal })
      .then((value) => {
        if (!abort.signal.aborted) setState(value);
      })
      .catch((cause) => {
        if (!abort.signal.aborted) setError((cause as Error).message);
      });
    return () => {
      active.current = false;
      abort.abort();
    };
  }, [api, path]);
  async function install() {
    if (!state?.eligible || !confirmed || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    if (command.current.path !== path)
      command.current = { path, key: crypto.randomUUID() };
    try {
      const result = await api<{
        connectionDetailsStored: boolean;
        extensionDigits: string;
        publicDidRetargeted: boolean;
      }>(path, {
        method: "POST",
        csrf,
        body: {
          requestKey: command.current.key,
          expectedConnectionVersion: 1,
          confirmed: true,
        },
      });
      if (
        !result.connectionDetailsStored ||
        result.publicDidRetargeted !== false ||
        result.extensionDigits !== state.extensionDigits
      )
        throw new Error(
          "The install result could not be confirmed. Retry without changing this assignment.",
        );
      if (!active.current) return;
      setState({ ...state, eligible: false, configured: true });
      setNotice(
        `Extension ${result.extensionDigits} is configured. Open Show connection details below, connect Tailscale, and enter the settings in your softphone. Registration and audio still need a handset test.`,
      );
      onInstalled();
    } catch (cause) {
      if (active.current) setError((cause as Error).message);
    } finally {
      lock.current = false;
      if (active.current) setBusy(false);
    }
  }
  if (state && !state.eligible && !notice) return null;
  return (
    <section aria-label="Tailscale softphone setup">
      {error && (
        <p role="alert" className="cr-notice error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="cr-notice">
          {notice}
        </p>
      )}
      {state?.eligible && (
        <>
          <h4>
            {state.existing
              ? "Issue existing softphone details"
              : "Install this softphone"}
          </h4>
          <p>
            Extension {state.extensionDigits} uses the company’s existing
            outgoing number. This does not move incoming calls to this phone.
            Tailscale must be connected; public SIP registration is not allowed.
          </p>
          <label className="cr-check">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I have checked this phone’s physical service location. Verify its
            carrier emergency address and installed route before issuing
            connection details.
          </label>
          <button
            type="button"
            className="cr-button"
            disabled={!confirmed || busy}
            onClick={() => void install()}
          >
            {busy
              ? "Checking and configuring…"
              : state.existing
                ? "Issue existing connection details"
                : "Install and issue connection details"}
          </button>
        </>
      )}
    </section>
  );
}
